import React, { useState, lazy } from 'react';
import { XCircle, Phone, Truck, Sparkles, CheckCircle2, AlertTriangle, CalendarX, UserX, Wrench, ChevronDown, Quote, Star, ShieldCheck, BadgeCheck, Zap, Award } from 'lucide-react';
import { FadeIn, StaggerContainer, StaggerItem } from './Animations';
export const TrustBar = () => {
  const items = [{
    icon: ShieldCheck,
    label: 'Licensed & Insured'
  }, {
    icon: Zap,
    label: 'Same-Day Service'
  }, {
    icon: BadgeCheck,
    label: 'Free Estimates'
  }, {
    icon: Award,
    label: 'Locally Owned'
  }];
  return <section className="bg-white border-b border-zinc-200" data-id="element-60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-61">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-62">
          {items.map((item, i) => <div key={i} className="flex items-center gap-3 justify-center md:justify-start" data-id="element-63">
              <div className="w-10 h-10 bg-brand-red/10 flex items-center justify-center rounded-sm shrink-0" data-id="element-64">
                <item.icon className="w-5 h-5 text-brand-red" data-id="element-65" />
              </div>
              <span className="font-oswald font-bold uppercase text-brand-black text-sm md:text-base tracking-wide" data-id="element-66">
                {item.label}
              </span>
            </div>)}
        </div>
      </div>
    </section>;
};
export const ProblemSection = () => <section className="py-20 md:py-24 bg-white relative overflow-hidden" data-id="element-67">
    <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-brand-red/5 rounded-full blur-3xl pointer-events-none" data-id="element-68" />
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-69">
      <div className="grid lg:grid-cols-12 gap-12 items-center" data-id="element-70">
        <FadeIn className="lg:col-span-5" data-id="element-71">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red mb-4" data-id="element-72">
            The Problem
          </div>
          <h2 className="font-oswald text-4xl md:text-5xl lg:text-6xl font-bold uppercase leading-[0.95] text-brand-black" data-id="element-73">
            You Know <br data-id="element-74" />{' '}
            <span className="text-brand-red" data-id="element-75">That Feeling.</span>
          </h2>
          <div className="mt-6 flex items-center gap-3 text-zinc-600" data-id="element-76">
            <AlertTriangle className="w-5 h-5 text-brand-red" data-id="element-77" />
            <span className="font-semibold" data-id="element-78">Sound familiar?</span>
          </div>
        </FadeIn>

        <FadeIn delay={0.1} className="lg:col-span-7 space-y-5 text-zinc-600 text-lg leading-relaxed" data-id="element-79">
          <p data-id="element-80">
            You walk past it every morning. Maybe it's the garage packed
            wall-to-wall with broken appliances and boxes of stuff you'll{' '}
            <em data-id="element-81">"sort through someday."</em> Maybe it's a construction project
            that left a mountain of debris.
          </p>
          <p data-id="element-82">
            Whatever it is — it's there. Every day. Taking up space in your life
            and in your head.
          </p>
          <p className="border-l-4 border-brand-red pl-6 py-2 bg-zinc-50 rounded-r-sm" data-id="element-83">
            You've told yourself a hundred times you'd handle it. But weekends
            come and go.{' '}
            <strong className="text-brand-black" data-id="element-84">The pile stays.</strong> And
            meanwhile, you're paying for it in stress, lost square footage, and
            that nagging guilt.
          </p>
        </FadeIn>
      </div>
    </div>
  </section>;
export const LogisticsTrapSection = () => {
  const traps = [{
    icon: CalendarX,
    title: 'City Pickup',
    desc: 'Only takes certain items on certain days. Restrictive.'
  }, {
    icon: AlertTriangle,
    title: 'Dumpster Rental',
    desc: 'Expensive, slow, and you do all the heavy lifting yourself.'
  }, {
    icon: UserX,
    title: 'Friends & Family',
    desc: 'They say yes, then never show up when you need them.'
  }, {
    icon: Wrench,
    title: 'Do It Yourself',
    desc: 'Your back pays the price for a week — and the job is half done.'
  }];
  return <section className="py-20 md:py-24 bg-zinc-100 relative overflow-hidden" data-id="element-85">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-86">
        <FadeIn className="text-center max-w-3xl mx-auto mb-14" data-id="element-87">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red mb-4" data-id="element-88">
            The Logistics Trap
          </div>
          <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase text-brand-black mb-5 leading-tight" data-id="element-89">
            Why That Junk Is <span className="text-brand-red" data-id="element-90">Still There</span>
          </h2>
          <p className="text-zinc-600 text-lg" data-id="element-91">
            It's not because you're lazy. It's because hauling junk is genuinely
            hard — and every "solution" out there makes it harder. We call it
            the <strong data-id="element-92">Logistics Trap.</strong>
          </p>
        </FadeIn>

        <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-93">
          {traps.map((trap, idx) => <StaggerItem key={idx} className="group bg-white p-7 rounded-sm border border-zinc-200 hover:border-brand-red transition-all hover:shadow-xl hover:-translate-y-1 duration-300 relative overflow-hidden" data-id="element-94">
              <div className="absolute top-0 right-0 w-16 h-16 bg-brand-red/5 group-hover:bg-brand-red/10 transition-colors" style={{
            clipPath: 'polygon(100% 0, 0 0, 100% 100%)'
          }} data-id="element-95"></div>
              <div className="relative" data-id="element-96">
                <div className="w-12 h-12 bg-zinc-100 group-hover:bg-brand-red/10 flex items-center justify-center rounded-sm mb-4 transition-colors" data-id="element-97">
                  <trap.icon className="w-6 h-6 text-zinc-500 group-hover:text-brand-red transition-colors" data-id="element-98" />
                </div>
                <h3 className="font-oswald text-xl font-bold uppercase text-brand-black mb-2" data-id="element-99">
                  {trap.title}
                </h3>
                <p className="text-zinc-600 text-sm leading-relaxed" data-id="element-100">
                  {trap.desc}
                </p>
              </div>
            </StaggerItem>)}
        </StaggerContainer>

        <FadeIn delay={0.4} className="mt-14 text-center max-w-2xl mx-auto bg-brand-black text-white p-8 rounded-sm relative overflow-hidden" data-id="element-101">
          <div className="absolute top-0 left-0 w-full h-1 bg-brand-red" data-id="element-102"></div>
          <p className="font-oswald text-2xl md:text-3xl font-bold uppercase leading-tight" data-id="element-103">
            The system is broken. <br data-id="element-104" />
            <span className="text-brand-red" data-id="element-105">Jez Junk Away changed that.</span>
          </p>
        </FadeIn>
      </div>
    </section>;
};
export const PayoffSection = () => <section className="py-24 md:py-32 bg-brand-black text-white relative overflow-hidden" data-id="element-106">
    {/* Background pattern */}
    <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='100' viewBox='0 0 56 100'%3E%3Cpath fill='%23ffffff' d='M28 66L0 50V16l28-16 28 16v34L28 66zM28 0L4 13.86v32.28L28 60l24-13.86V13.86L28 0z'/%3E%3C/svg%3E")`,
    backgroundSize: '40px'
  }} data-id="element-107" />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-red/10 rounded-full blur-3xl pointer-events-none" data-id="element-108" />

    {/* Watermark logo */}
    <img src="https://cdn.magicpatterns.com/uploads/vWKqmdVYNC9wCEg3vWukxp/Photoroom_20260602_081134.png" alt="" aria-hidden="true" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-[700px] opacity-[0.07] pointer-events-none select-none" data-id="element-109" />

    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-110">
      <FadeIn data-id="element-111">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-brand-red mb-5" data-id="element-112">
          The Result
        </div>
        <h2 className="font-oswald text-4xl md:text-6xl lg:text-7xl font-bold uppercase mb-8 leading-[0.95]" data-id="element-113">
          Imagine Tomorrow <br data-id="element-114" />
          <span className="text-brand-red" data-id="element-115">Morning...</span>
        </h2>
        <p className="text-xl md:text-2xl text-zinc-200 mb-6 leading-relaxed font-light" data-id="element-116">
          You walk outside. Or into your garage. Or through your backyard.
        </p>
        <p className="font-oswald text-3xl md:text-5xl font-bold uppercase text-white mb-8" data-id="element-117">
          And it's <span className="text-brand-red" data-id="element-118">clean.</span>
        </p>
        <p className="text-base md:text-lg text-zinc-400 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-119">
          No pile. No debris. No overgrown mess. Just open, clean, usable space
          — your space — back in your hands. You make one call, we handle
          everything.
        </p>
        <a href="tel:+15597709442" className="inline-flex items-center justify-center gap-3 bg-brand-red hover:bg-brand-darkred text-white px-10 py-5 rounded-sm font-bold text-lg transition-all uppercase tracking-wide shadow-2xl shadow-brand-red/30 hover:shadow-brand-red/50 hover:scale-105 duration-300" data-id="element-120">
          <Phone className="w-5 h-5" data-id="element-121" />
          Claim Your Space Back Today
        </a>
      </FadeIn>
    </div>
  </section>;
export const ProcessSection = () => {
  const steps = [{
    num: '01',
    icon: Phone,
    title: 'Call or Text a Photo',
    desc: 'Call or text (559) 770-9442 — send us a photo of what you have for the fastest, most accurate quote. Straight, honest pricing in minutes. No games, no pressure.'
  }, {
    num: '02',
    icon: Truck,
    title: 'We Show Up & Handle It',
    desc: "Our crew arrives on time, ready to work. We do all the lifting, loading, hauling, and final cleanup. You don't touch a thing."
  }, {
    num: '03',
    icon: Sparkles,
    title: 'Enjoy Your Clean Space',
    desc: "We leave. Your space is cleared. You breathe. You wonder why you didn't call sooner. That's the Jez difference."
  }];
  return <section id="process" className="py-20 md:py-24 bg-white" data-id="element-122">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-123">
        <FadeIn className="text-center max-w-2xl mx-auto mb-16" data-id="element-124">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red mb-4" data-id="element-125">
            How It Works
          </div>
          <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase text-brand-black leading-tight" data-id="element-126">
            Three Simple Steps <br data-id="element-127" /> to a{' '}
            <span className="text-brand-red" data-id="element-128">Clean Space</span>
          </h2>
        </FadeIn>

        <StaggerContainer className="grid md:grid-cols-3 gap-6 lg:gap-10 relative" data-id="element-129">
          {/* Connecting line */}
          <div className="hidden md:block absolute top-20 left-[16%] right-[16%] h-px border-t-2 border-dashed border-zinc-200 -z-10" data-id="element-130"></div>

          {steps.map((step, idx) => <StaggerItem key={idx} className="bg-white p-8 rounded-sm border-2 border-zinc-100 text-center relative hover:border-brand-red transition-all hover:shadow-xl group" data-id="element-131">
              <div className="font-oswald text-7xl md:text-8xl font-bold text-zinc-100 group-hover:text-brand-red/20 leading-none transition-colors absolute top-4 right-6 select-none" data-id="element-132">
                {step.num}
              </div>
              <div className="relative" data-id="element-133">
                <div className="w-16 h-16 bg-brand-red/10 group-hover:bg-brand-red flex items-center justify-center rounded-sm mx-auto mb-5 transition-colors" data-id="element-134">
                  <step.icon className="w-7 h-7 text-brand-red group-hover:text-white transition-colors" data-id="element-135" />
                </div>
                <h3 className="font-oswald text-2xl font-bold uppercase text-brand-black mb-3" data-id="element-136">
                  {step.title}
                </h3>
                <p className="text-zinc-600 leading-relaxed" data-id="element-137">{step.desc}</p>
              </div>
            </StaggerItem>)}
        </StaggerContainer>
      </div>
    </section>;
};
export const TestimonialsSection = () => {
  const reviews = [{
    quote: "I had a rental property that was absolutely trashed by the previous tenants. Appliances, trash, furniture — everywhere. Jez Junk Away showed up and had it completely cleared out in no time. I couldn't believe how fast and professional they were.",
    name: 'Michael R.',
    role: 'Property Owner',
    location: 'Fresno, CA'
  }, {
    quote: 'My backyard had years of debris built up. Branches, leaves, old garden equipment — it was a disaster. One call and it was completely cleaned up. They even did the seasonal cleanup on the shrubs. Amazing service.',
    name: 'Sarah K.',
    role: 'Homeowner',
    location: 'Clovis, CA'
  }, {
    quote: 'We needed construction debris removed from a commercial renovation. They handled everything — concrete, drywall, lumber — cleared it all out so our crew could keep working. Reliable, fast, and fairly priced.',
    name: 'James T.',
    role: 'General Contractor',
    location: 'Visalia, CA'
  }];
  return <section id="reviews" className="py-20 md:py-24 bg-zinc-100" data-id="element-138">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-139">
        <FadeIn className="text-center max-w-2xl mx-auto mb-14" data-id="element-140">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red mb-4" data-id="element-141">
            Real Customers
          </div>
          <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase text-brand-black leading-tight" data-id="element-142">
            What Our <span className="text-brand-red" data-id="element-143">Clients Say</span>
          </h2>
          <div className="flex items-center justify-center gap-2 mt-5" data-id="element-144">
            <div className="flex" data-id="element-145">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" data-id="element-146" />)}
            </div>
            <span className="font-bold text-brand-black ml-1" data-id="element-147">5.0</span>
            <span className="text-zinc-600" data-id="element-148">· 500+ happy customers</span>
          </div>
        </FadeIn>

        <StaggerContainer className="grid md:grid-cols-3 gap-6" data-id="element-149">
          {reviews.map((r, i) => <StaggerItem key={i} className="bg-white p-8 rounded-sm shadow-sm border border-zinc-200 relative hover:shadow-xl transition-shadow group" data-id="element-150">
              <Quote className="absolute -top-3 -left-3 w-10 h-10 text-brand-red bg-white p-1.5 rounded-sm" data-id="element-151" />
              <div className="flex gap-0.5 mb-4" data-id="element-152">
                {[...Array(5)].map((_, idx) => <Star key={idx} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-153" />)}
              </div>
              <p className="text-zinc-700 leading-relaxed mb-6 text-[15px]" data-id="element-154">
                "{r.quote}"
              </p>
              <div className="pt-5 border-t border-zinc-100 flex items-center gap-3" data-id="element-155">
                <div className="w-11 h-11 bg-brand-black text-white font-oswald font-bold flex items-center justify-center rounded-sm" data-id="element-156">
                  {r.name.charAt(0)}
                </div>
                <div data-id="element-157">
                  <p className="font-oswald font-bold text-brand-black uppercase text-sm" data-id="element-158">
                    {r.name}
                  </p>
                  <p className="text-xs text-zinc-500" data-id="element-159">
                    {r.role} · {r.location}
                  </p>
                </div>
              </div>
            </StaggerItem>)}
        </StaggerContainer>
      </div>
    </section>;
};
export const ComparisonSection = () => {
  const comparisons = [{
    good: 'A local team that actually shows up on time',
    bad: 'No-shows or constant rescheduling'
  }, {
    good: 'Removal of ANY item, from ANY location',
    bad: '"Sorry, we can\'t take that"'
  }, {
    good: 'Fast, professional, careful service',
    bad: 'Slow, lazy, careless crews'
  }, {
    good: 'Free, transparent, no-obligation estimate',
    bad: 'Surprise fees and hidden charges'
  }, {
    good: 'Clean, thorough job completion guaranteed',
    bad: 'Half-done work left behind'
  }, {
    good: 'Friendly, respectful, vetted team',
    bad: "Strangers you don't feel safe around"
  }];
  return <section className="py-20 md:py-24 bg-white" data-id="element-160">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-161">
        <FadeIn className="text-center mb-12" data-id="element-162">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red mb-4" data-id="element-163">
            The Difference
          </div>
          <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase text-brand-black leading-tight" data-id="element-164">
            Why Central Valley <br data-id="element-165" />{' '}
            <span className="text-brand-red" data-id="element-166">Chooses Jez</span>
          </h2>
        </FadeIn>

        <FadeIn className="bg-white rounded-sm shadow-2xl border border-zinc-200 overflow-hidden" data-id="element-167">
          <div className="grid grid-cols-2 bg-brand-black text-white" data-id="element-168">
            <div className="p-5 md:p-6 border-r border-zinc-800 flex items-center gap-3" data-id="element-169">
              <CheckCircle2 className="w-5 h-5 text-brand-red shrink-0" data-id="element-170" />
              <span className="font-oswald text-base md:text-lg uppercase font-bold" data-id="element-171">
                What You Get with Jez
              </span>
            </div>
            <div className="p-5 md:p-6 flex items-center gap-3 bg-zinc-900/50" data-id="element-172">
              <XCircle className="w-5 h-5 text-zinc-500 shrink-0" data-id="element-173" />
              <span className="font-oswald text-base md:text-lg uppercase font-bold text-zinc-400" data-id="element-174">
                What You Won't Get
              </span>
            </div>
          </div>
          <div className="divide-y divide-zinc-100" data-id="element-175">
            {comparisons.map((item, idx) => <div key={idx} className="grid grid-cols-2" data-id="element-176">
                <div className="p-4 md:p-6 flex items-start gap-3 border-r border-zinc-100" data-id="element-177">
                  <CheckCircle2 className="w-5 h-5 text-brand-red shrink-0 mt-0.5" data-id="element-178" />
                  <span className="font-semibold text-brand-black text-sm md:text-base" data-id="element-179">
                    {item.good}
                  </span>
                </div>
                <div className="p-4 md:p-6 flex items-start gap-3 bg-zinc-50" data-id="element-180">
                  <XCircle className="w-5 h-5 text-zinc-400 shrink-0 mt-0.5" data-id="element-181" />
                  <span className="text-zinc-500 text-sm md:text-base line-through decoration-zinc-300" data-id="element-182">
                    {item.bad}
                  </span>
                </div>
              </div>)}
          </div>
        </FadeIn>
      </div>
    </section>;
};
export const FinalCTASection = () => <section className="relative py-20 md:py-28 bg-brand-red text-white overflow-hidden" data-id="element-183">
    {/* Background pattern */}
    <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='100' viewBox='0 0 56 100'%3E%3Cpath fill='%23000000' d='M28 66L0 50V16l28-16 28 16v34L28 66zM28 0L4 13.86v32.28L28 60l24-13.86V13.86L28 0z'/%3E%3C/svg%3E")`,
    backgroundSize: '40px'
  }} data-id="element-184" />
    {/* Watermark logo */}
    <img src="https://cdn.magicpatterns.com/uploads/vWKqmdVYNC9wCEg3vWukxp/Photoroom_20260602_081134.png" alt="" aria-hidden="true" className="absolute -right-20 top-1/2 -translate-y-1/2 w-[500px] opacity-10 pointer-events-none select-none hidden md:block" data-id="element-185" />
    <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-186">
      <FadeIn data-id="element-187">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-white/70 mb-5" data-id="element-188">
          Don't Wait Another Day
        </div>
        <h2 className="font-oswald text-4xl md:text-6xl lg:text-7xl font-bold uppercase leading-[0.95] mb-6" data-id="element-189">
          That Pile Won't <br data-id="element-190" /> Move Itself.
        </h2>
        <p className="text-lg md:text-xl text-red-50 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-191">
          Call us or <strong className="text-white" data-id="element-192">text a photo</strong> of
          what needs to go — we'll give you a quote in minutes. Free, no
          obligation. We have openings this week.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-193">
          <a href="tel:+15597709442" className="group flex items-center justify-center gap-3 bg-white text-brand-red hover:bg-brand-black hover:text-white px-10 py-5 rounded-sm font-oswald font-bold text-2xl transition-all uppercase tracking-wide shadow-2xl" data-id="element-194">
            <Phone className="w-6 h-6 group-hover:rotate-12 transition-transform" data-id="element-195" />
            (559) 770-9442
          </a>
          <div className="text-left" data-id="element-196">
            <p className="text-white font-bold" data-id="element-197">Call or text 24/7</p>
            <p className="text-red-100 text-sm" data-id="element-198">
              Free estimates · No obligation
            </p>
          </div>
        </div>
      </FadeIn>
    </div>
  </section>;
export const FAQSection = () => {
  const faqs = [{
    q: 'Is this going to be expensive?',
    a: 'We offer free estimates with zero obligation — and the easiest way is to text a photo of the items to (559) 770-9442. You know the price before you commit to anything. Our pricing is fair, transparent, and competitive. No surprises, ever.'
  }, {
    q: "I don't know if you can take what I have",
    a: 'We remove appliances, furniture, construction debris, yard waste, commercial junk, demolition material — if it takes up space, we take it. Call us and ask about your specific items.'
  }, {
    q: 'Can I trust strangers on my property?',
    a: "We're a local Central Valley business with a reputation built on trust, professionalism, and doing the job right every single time. Our crew is vetted, friendly, and respectful of your property."
  }, {
    q: "What if it's a really big job?",
    a: 'The bigger the job, the more you need us. We handle large-scale commercial cleanouts, full property cleanups, and major debris removal regularly. No job is too big.'
  }, {
    q: 'How fast can you come?',
    a: "We move fast. Contact us today and we'll work to get you scheduled as quickly as possible — often within the same week, sometimes same-day depending on availability."
  }, {
    q: 'Do I have to be home?',
    a: "Not necessarily. We can coordinate the details with you in advance and handle the job without you needing to supervise every minute. We treat your property with respect whether you're there or not."
  }];
  const [open, setOpen] = useState<number | null>(0);
  return <section id="faq" className="py-20 md:py-24 bg-zinc-100" data-id="element-199">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-200">
        <FadeIn className="text-center mb-12" data-id="element-201">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red mb-4" data-id="element-202">
            Got Questions?
          </div>
          <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase text-brand-black leading-tight" data-id="element-203">
            Frequently Asked <br data-id="element-204" />{' '}
            <span className="text-brand-red" data-id="element-205">Questions</span>
          </h2>
        </FadeIn>

        <StaggerContainer className="space-y-3" data-id="element-206">
          {faqs.map((faq, idx) => {
          const isOpen = open === idx;
          return <StaggerItem key={idx} data-id="element-207">
                <button onClick={() => setOpen(isOpen ? null : idx)} className={`w-full text-left bg-white rounded-sm border-l-4 transition-all overflow-hidden ${isOpen ? 'border-brand-red shadow-lg' : 'border-zinc-300 hover:border-brand-red'}`} data-id="element-208">
                  <div className="flex items-center justify-between p-5 md:p-6 gap-4" data-id="element-209">
                    <h3 className="font-oswald text-lg md:text-xl font-bold uppercase text-brand-black" data-id="element-210">
                      {faq.q}
                    </h3>
                    <ChevronDown className={`w-5 h-5 text-brand-red shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-211" />
                  </div>
                  <div className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-212">
                    <div className="overflow-hidden" data-id="element-213">
                      <p className="px-5 md:px-6 pb-6 text-zinc-600 leading-relaxed" data-id="element-214">
                        {faq.a}
                      </p>
                    </div>
                  </div>
                </button>
              </StaggerItem>;
        })}
        </StaggerContainer>

        <FadeIn delay={0.3} className="mt-12 text-center bg-brand-black text-white p-8 rounded-sm" data-id="element-215">
          <p className="font-oswald text-xl md:text-2xl font-bold uppercase mb-4" data-id="element-216">
            Still have questions?
          </p>
          <a href="tel:+15597709442" className="inline-flex items-center gap-2 bg-brand-red hover:bg-brand-darkred text-white px-8 py-4 rounded-sm font-bold uppercase tracking-wide transition-colors" data-id="element-217">
            <Phone className="w-5 h-5" data-id="element-218" />
            Call (559) 770-9442
          </a>
        </FadeIn>
      </div>
    </section>;
};